import React from "react";
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaWhatsapp, FaMapMarkerAlt, FaPhone, FaEnvelope } from "react-icons/fa";
import { InView } from 'react-intersection-observer';
import car from '../images/carrentallogo.png'; // Import the logo image

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: <FaFacebook className="text-white group-hover:text-blue-500 transition-colors duration-300" />, href: "#", label: "Facebook" },
    { icon: <FaTwitter className="text-white group-hover:text-blue-400 transition-colors duration-300" />, href: "#", label: "Twitter" },
    { icon: <FaInstagram className="text-white group-hover:text-pink-500 transition-colors duration-300" />, href: "#", label: "Instagram" },
    { icon: <FaLinkedin className="text-white group-hover:text-blue-300 transition-colors duration-300" />, href: "#", label: "LinkedIn" },
    { icon: <FaWhatsapp className="text-white group-hover:text-green-500 transition-colors duration-300" />, href: "#", label: "WhatsApp" }
  ];

  const footerSections = [
    {
      title: "Quick Links",
      links: [
        { name: "Home", href: "/" },
        { name: "Our Story", href: "/our-story" },
        { name: "Our Fleet", href: "/fleet" },
        { name: "Book a Car", href: "/booking" }
      ]
    },
    {
      title: "Services",
      links: [
        { name: "Rental Terms", href: "/rental-terms" },
        { name: "FAQs", href: "/faq" },
        { name: "Customer Support", href: "/support" },
        { name: "Help Center", href: "/help-center" }
      ]
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", href: "/privacy-policy" },
        { name: "Terms & Conditions", href: "/terms-and-conditions" },
        { name: "Shipping Policy", href: "/shipping-policy" },
        { name: "Cancellation & Refunds", href: "/cancellation-refund" }
      ]
    }
  ];

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-purple-900 to-blue-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500 rounded-full mix-blend-screen filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          {/* Top Section - Brand & Contact */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Brand Section with Logo */}
            <InView triggerOnce={true} threshold={0.3}>
              {({ inView, ref }) => (
                <div ref={ref} className={`${inView ? 'animate__animated animate__fadeInLeft' : ''}`}>
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    {/* Logo and Company Name */}
                    <div className="flex items-center mb-4">
                      <img 
                        src={car} 
                        alt="Nupura Cars Logo" 
                        className="w-16 h-16 rounded-full object-cover border-2 border-cyan-400 mr-4"
                      />
                      <div>
                        <h3 className="text-2xl font-bold text-white">
                          Nupura <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Cars</span>
                        </h3>
                        <p className="text-blue-200 text-sm">Premium Car Rentals</p>
                      </div>
                    </div>
                    
                    <p className="text-blue-200 leading-relaxed mb-6">
                      Your trusted partner for reliable and affordable car rentals. Explore a wide range of vehicles and drive with comfort, safety, and convenience.
                    </p>
                    
                    {/* Contact Info */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3 text-blue-200">
                        <FaPhone className="w-4 h-4 text-cyan-400" />
                        <span>+91 96663 17749</span>
                      </div>
                      <div className="flex items-center space-x-3 text-blue-200">
                        <FaEnvelope className="w-4 h-4 text-cyan-400" />
                        <span>contact.nupuracars@gmail.com</span>
                      </div>
                      <div className="flex items-start space-x-3 text-blue-200">
                        <FaMapMarkerAlt className="w-4 h-4 text-cyan-400 mt-1" />
                        <span className="text-sm">H. No. 105, Near to SBI Bank, Abhyudaya Nagar-Chintalakunta, LB Nagar, Hyderabad-500074, Telangana</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </InView>

            {/* Links Sections */}
            <InView triggerOnce={true} threshold={0.3}>
              {({ inView, ref }) => (
                <div ref={ref} className={`lg:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-8 ${inView ? 'animate__animated animate__fadeInRight' : ''}`}>
                  {footerSections.map((section, index) => (
                    <div key={index} className="space-y-4">
                      <h4 className="text-lg font-bold text-white border-b border-cyan-400/30 pb-2">
                        {section.title}
                      </h4>
                      <ul className="space-y-3">
                        {section.links.map((link, linkIndex) => (
                          <li key={linkIndex}>
                            <a 
                              href={link.href}
                              className="text-blue-200 hover:text-cyan-400 transition-all duration-300 flex items-center group"
                            >
                              <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 group-hover:scale-125 transition-transform duration-300"></span>
                              {link.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </InView>
          </div>

          {/* Newsletter & Social Section */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div ref={ref} className={`${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
                <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-md rounded-2xl p-8 border border-cyan-400/30">
                  <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                    {/* Newsletter */}
                    <div className="flex-1 text-center lg:text-left">
                      <h4 className="text-xl font-bold text-white mb-2">Stay Updated</h4>
                      <p className="text-blue-200">Get the latest offers and updates directly in your inbox</p>
                    </div>
                    
                    {/* Newsletter Form */}
                    <div className="flex-1 w-full lg:max-w-md">
                      <div className="flex space-x-2">
                        <input 
                          type="email" 
                          placeholder="Enter your email"
                          className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:border-cyan-400 transition-all duration-300"
                        />
                        <button className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                          Subscribe
                        </button>
                      </div>
                    </div>

                    {/* Social Links */}
                    <div className="flex space-x-4">
                      {socialLinks.map((social, index) => (
                        <a
                          key={index}
                          href={social.href}
                          aria-label={social.label}
                          className="group w-12 h-12 bg-white/10 hover:bg-cyan-500/20 backdrop-blur-sm rounded-xl border border-white/20 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:border-cyan-400/50"
                        >
                          {social.icon}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </InView>
        </div>

        {/* Bottom Bar */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`border-t border-white/20 pt-8 pb-6 ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                {/* Copyright with Logo */}
                <div className="flex items-center space-x-4 text-center lg:text-left">
                  <img 
                    src={car} 
                    alt="Nupura Cars Logo" 
                    className="w-10 h-10 rounded-full object-cover border border-cyan-400 hidden sm:block"
                  />
                  <p className="text-blue-200 text-sm">
                    © {currentYear}{' '}
                    <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent font-bold">
                      Nupura Cars
                    </span>
                    . All Rights Reserved.
                  </p>
                </div>

                {/* Additional Links */}
                <div className="flex flex-wrap justify-center gap-6 text-sm">
                  <a href="/privacy-policy" className="text-blue-200 hover:text-cyan-400 transition-colors duration-300">
                    Privacy Policy
                  </a>
                  <a href="/terms-and-conditions" className="text-blue-200 hover:text-cyan-400 transition-colors duration-300">
                    Terms of Service
                  </a>
                  <a href="/contact" className="text-blue-200 hover:text-cyan-400 transition-colors duration-300">
                    Contact Us
                  </a>
                </div>

                {/* Payment Methods */}
                <div className="flex items-center space-x-2 text-blue-200 text-sm">
                  <span>Secure Payments:</span>
                  <div className="flex space-x-1">
                    {["💳", "🔒", "💰"].map((icon, index) => (
                      <span key={index} className="text-lg">{icon}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </InView>
      </div>

      {/* Floating Elements */}
      <div className="absolute bottom-10 left-10 w-4 h-4 bg-cyan-400 rounded-full opacity-50 animate-ping"></div>
      <div className="absolute top-10 right-10 w-3 h-3 bg-purple-400 rounded-full opacity-70 animate-bounce"></div>
    </footer>
  );
};

export default Footer;